// =======================================================================================================
import { ReactNode, Component, PureComponent, ReactElement } from 'react';
import _ from 'lodash';
import { isString, isDefined, IsNullOrEmpty } from "@Utils/Micro";
import { DebugEx } from "@Utils/DebugEx"
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { IWidgetThingsClass } from '../Utils/IWidgetThingsClass';
import { WebThing, WebThingPort } from '@API/WebThing';
import { WebThingPortType } from '@API/WebNodeTypes';
import { ReactJsonView } from '@Primitive/ReactJsonView';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing } from '@UI/Widgets/WidgetStructs';
import { SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { SettingsList } from '@Primitive/SettingsList';
import { Plegma } from '@API/Api';
import { LocalizationMap } from '@Primitive/Localization/Localization';
// =======================================================================================================
export interface JsonViewerData {
    DisplayDataType: boolean;
    DisplayObjectSize: boolean;
    IndentWidth: number;
    CollapseStringsAfter: number; // -1 is mean disabled
    EnableClipboard: boolean;
    Collapsed: number;

    // TODO: add editing
}
// ------------------------------------------------------------------------------------------------------
interface JsonViewerState {
    json: object;
}
// ------------------------------------------------------------------------------------------------------
class JsonViewer extends Component<WidgetsThingsStructs.PartialIWidgetThingsProps<JsonViewerData>, JsonViewerState> {
    // ---------------------------------------------------------------------------------------------------
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<JsonViewerData>) {
        super(props);
        this.state = {
            json: {}
        };
    }
    // ---------------------------------------------------------------------------------------------------
    private UpdateDataFromPort(data: any) {
        try {
            if (IsNullOrEmpty(data)) {
                this.setState({ json: {} });
            } else if (isString(data)) {
                let value = JSON.parse(data);
                this.setState({ json: value });
            } else
                this.setState({ json: data });
        } catch (ex) {
            DebugEx.TraceError(ex);
        }
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: WidgetsThingsStructs.PartialIWidgetThingsProps<JsonViewerData>) {
        if (!_.isEqual(this.props.WidgetData, prevProps.WidgetData) ||
            this.props.PortVersion != prevProps.PortVersion) {
            _.forEach(this.props.PortsStates, (port, key) => {
                if (key == "json")
                    this.UpdateDataFromPort(port.State);
            });
        }
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidMount() {
        // Update port
        _.forEach(this.props.PortsStates, (port, key) => {
            if (key == "json")
                this.UpdateDataFromPort(port.State);
        });
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        return <ReactJsonView
            key={"json-viewer-" + this.props.key}
            value={this.state.json}
            displayDataTypes={this.props.WidgetData.DisplayDataType}
            displaySize={this.props.WidgetData.DisplayObjectSize}
            collapseStringsAfterLength={this.props.WidgetData.CollapseStringsAfter}
            enableClipboard={this.props.WidgetData.EnableClipboard}
            indentWidth={this.props.WidgetData.IndentWidth}
            defaultInspectDepth={this.props.WidgetData.Collapsed}
            style={{ minHeight: "100%" }}
        />;
    }
    // ---------------------------------------------------------------------------------------------------
}
// =======================================================================================================
// Settings
// ---------------------------------------------------------------------------------------------------
class JsonViewerSettings extends PureComponent<IWidgetSettingsProps<JsonViewerData>> {
    constructor(props: IWidgetSettingsProps<JsonViewerData>) {
        super(props);
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        let settings: SettingsGroup<JsonViewerData>[] = [
            {
                name: "",
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: "Display Data Type",
                        tooltip: 'When set to true, data type labels prefix values.',
                        dataPath: "DisplayDataType"
                    },
                    {
                        type: SettingsType.Boolean,
                        label: "Display Object Size",
                        tooltip: 'When set to true, objects and arrays are labeled with size.',
                        dataPath: "DisplayObjectSize"
                    },
                    {
                        type: SettingsType.Boolean,
                        label: "Enable Clipboard",
                        tooltip: 'When set to true, you copy objects and arrays to clipboard by clicking on the clipboard icon.',
                        dataPath: "EnableClipboard"
                    },
                    {
                        type: SettingsType.DropDown,
                        label: "Indent",
                        tooltip: 'Set the indent-width for nested objects.',
                        dataPath: "IndentWidth",
                        items: [
                            { value: 0, label: '0' },
                            { value: 1, label: '1' },
                            { value: 2, label: '2' },
                            { value: 3, label: '3' },
                            { value: 4, label: '4' },
                            { value: 5, label: '5' }
                        ]
                    },
                    {
                        type: SettingsType.DropDown,
                        label: "Collapse strings after",
                        tooltip: 'Specify threshold in which strings will be cut off at that length. Collapsed strings are followed by an ellipsis. String content can be expanded and collapsed by clicking on the string value',
                        dataPath: "CollapseStringsAfter",
                        items: [
                            { value: false, label: 'false' },
                            { value: 5, label: '5' },
                            { value: 10, label: '10' },
                            { value: 15, label: '15' },
                            { value: 20, label: '20' }
                        ]
                    },
                    {
                        type: SettingsType.DropDown,
                        label: "Collapsed",
                        tooltip: 'When set to true, all nodes will be collapsed by default. Use an integer value to collapse at a particular depth.',
                        dataPath: "Collapsed",
                        items: [
                            { value: false, label: 'false' },
                            { value: 0, label: '0' },
                            { value: 1, label: '1' },
                            { value: 2, label: '2' },
                            { value: 3, label: '3' }
                        ]
                    }
                ]
            }
        ]

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                if (isDefined(this.props.onWidgetDataUpdate))
                    this.props.onWidgetDataUpdate(data);
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
}

// =======================================================================================================
// Description interface
export class JsonViewerWidget extends IWidgetThingsClass<JsonViewerData> {
    // ------------------------------------------------------------------------------------------------
    public Render(key: string, width: number, height: number): ReactNode {
        return <JsonViewer />;
    }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): ReactElement<any> {
        const parent = this.Parent;
        return <JsonViewerSettings
            key={"Settings-" + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)} // copy of the data and is return the copy in the update cb
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Ports
        let ports: WebThingPort[] = [];

        // Json
        ports.push(new WebThingPort(
            {
                Name: "Json",
                PortKey: "json",
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ""
            },
            WebThingPortType.Data
        ));

        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): JsonViewerData {
        return {
            CollapseStringsAfter: 20,
            DisplayDataType: false,
            DisplayObjectSize: false,
            EnableClipboard: true,
            IndentWidth: 4,
            Collapsed: 2
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return JsonViewerWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let data: JsonViewerData = JsonViewerWidget.GetDefaultWidgetDataStatic();
        return {
            Title: "JSON Viewer",
            WidgetTitle: "",
            Key: "JsonViewerWidget",
            Type: "JsonViewerWidget",
            Data: data,
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + "/png/json.png"
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary(): LocalizationMap {
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            return {
                [WidgetsThingsStructs.WidgetLocalizationKeys.Title]: this.Description.WidgetTitle
            };
        else
            // nothing to expose
            return {};
    };
    // ------------------------------------------------------------------------------------------------
}
// =======================================================================================================